import { useApplicationConfiguration } from '../../../../hooks/useApplicationConfiguration';
import { useMemo } from 'react';
import { ImportedItemValidatorContext } from '../../../../lib/imports/importTypes';
import { EntityIdentificationType } from '@ydistri/api-sdk';

/**
 * Creates validator context based on identification types and MDB auto-increment settings.
 * This is a pure function that can be used both in hooks and regular functions.
 */
export const createImportedItemValidatorContext = (
  productIdentificationType: EntityIdentificationType,
  storeIdentificationType: EntityIdentificationType,
  mdbProductIdAutoIncrement: string,
  mdbWarehouseIdAutoIncrement: string,
): ImportedItemValidatorContext[] => {
  const result: ImportedItemValidatorContext[] = [];

  if (
    mdbProductIdAutoIncrement === 'INT' &&
    productIdentificationType === EntityIdentificationType.CustomerId
  ) {
    result.push({
      id: 'product',
      pattern: /^\d+$/, // integers only
      errorMessage: 'Product Id must contain only digits',
    });
  } else {
    result.push({
      id: 'product',
      pattern: /.+/, // any non-empty string
      errorMessage: 'Product Id cannot be empty',
    });
  }

  if (
    mdbWarehouseIdAutoIncrement === 'INT' &&
    storeIdentificationType === EntityIdentificationType.CustomerId
  ) {
    result.push({
      id: 'store',
      pattern: /^\d+$/, // integers only
      errorMessage: 'Store Id must contain only digits',
    });
  } else {
    result.push({
      id: 'store',
      pattern: /.+/, // any non-empty string
      errorMessage: 'Store Id cannot be empty',
    });
  }

  return result;
};

const useImportedItemValidatorContext = (
  productIdentificationType: EntityIdentificationType,
  storeIdentificationType: EntityIdentificationType,
): ImportedItemValidatorContext[] => {
  const { mdbProductIdAutoIncrement, mdbWarehouseIdAutoIncrement } = useApplicationConfiguration();

  return useMemo(
    () =>
      createImportedItemValidatorContext(
        productIdentificationType,
        storeIdentificationType,
        mdbProductIdAutoIncrement,
        mdbWarehouseIdAutoIncrement,
      ),
    [
      mdbProductIdAutoIncrement,
      mdbWarehouseIdAutoIncrement,
      productIdentificationType,
      storeIdentificationType,
    ],
  );
};

export default useImportedItemValidatorContext;
